/**
 * Profile Photo Cache Keys
 *
 * Centralized key generation for profile photo URL persistence.
 * Used by persistentCache to store photo URLs per user.
 *
 * V250: Canonical storage key is `profile_photo:${userId}` which maps to
 *       localStorage key `blissnet_cache_profile_photo:${userId}` via PersistentCache.
 *       Older builds used `profile_photo_url_${userId}` →
 *       `blissnet_cache_profile_photo_url_${userId}` (legacy key).
 */

import { persistentCache } from './persistentCache';

const PERSISTENT_CACHE_PREFIX = 'blissnet_cache_';
const LEGACY_PROFILE_PHOTO_URL_PREFIX = 'blissnet_cache_profile_photo_url_';

/**
 * Get the persistent cache key for a user's profile photo URL (canonical)
 * @param userId - User ID
 * @returns Cache key (e.g., "profile_photo:abc123")
 */
export const profilePhotoUrlKey = (userId: string): string => {
  // Canonical stable key shared with PhotoStore (profile_photo:${userId})
  return `profile_photo:${userId}`;
};

/**
 * Get the persistent cache key for a user's profile photo cache version
 * @param userId - User ID
 * @returns Cache key (e.g., "profile_photo_cv_abc123")
 */
export const profilePhotoCvKey = (userId: string): string => {
  return `profile_photo_cv_${userId}`;
};

/**
 * V250: Structured cache payload type
 * 
 * Never store raw strings like "__BLOB_CACHED__" that could be used as URLs.
 * Always use structured payloads that clearly indicate the source.
 */
export type ProfilePhotoCachePayload =
  | { kind: 'blob'; blobKey: string; timestamp: number; ttl: number }
  | { kind: 'url'; url: string; timestamp: number; ttl: number };

/**
 * V250: Helper to read structured cache payload from persistentCache
 * Returns null if cache is missing, expired, or invalid format
 */
export function getProfilePhotoCachePayload(userId: string): ProfilePhotoCachePayload | null {
  try {
    const raw = persistentCache.get<any>(profilePhotoUrlKey(userId));
    
    if (!raw) return null;
    
    // Handle legacy string format (migrate on read)
    if (typeof raw === 'string') {
      // Legacy "__BLOB_CACHED__" sentinel → convert to structured blob payload
      if (raw === '__BLOB_CACHED__') {
        const blobKey = profilePhotoUrlKey(userId);
        const payload: ProfilePhotoCachePayload = {
          kind: 'blob',
          blobKey,
          timestamp: Date.now(),
          ttl: 24 * 60 * 60 * 1000,
        };
        // Write back as structured format
        persistentCache.set(profilePhotoUrlKey(userId), payload, 24 * 60 * 60 * 1000);
        return payload;
      }
      // Legacy URL string → convert to structured url payload
      if (raw.startsWith('http')) {
        const payload: ProfilePhotoCachePayload = {
          kind: 'url',
          url: raw,
          timestamp: Date.now(),
          ttl: 24 * 60 * 60 * 1000,
        };
        // Write back as structured format
        persistentCache.set(profilePhotoUrlKey(userId), payload, 24 * 60 * 60 * 1000);
        return payload;
      }
      return null;
    }
    
    // Handle structured payload
    if (raw && typeof raw === 'object') {
      if (raw.kind === 'blob' && raw.blobKey) {
        return raw as ProfilePhotoCachePayload;
      }
      if (raw.kind === 'url' && typeof raw.url === 'string' && raw.url.startsWith('http')) {
        return raw as ProfilePhotoCachePayload;
      }
    }
    
    return null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ V250: Failed to read profile photo cache payload:', error);
    }
    return null;
  }
}

/**
 * V250: Helper to write structured cache payload to persistentCache
 */
export function setProfilePhotoCachePayload(
  userId: string,
  payload: ProfilePhotoCachePayload
): void {
  try {
    persistentCache.set(profilePhotoUrlKey(userId), payload, payload.ttl);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ V250: Failed to write profile photo cache payload:', error);
    }
  }
}

/**
 * V250: Helper to resolve cache payload to actual URL
 * NEVER returns "__BLOB_CACHED__" or any sentinel string.
 * Returns null if blob not available or payload invalid.
 */
export async function resolveProfilePhotoCacheUrl(
  userId: string
): Promise<string | null> {
  const payload = getProfilePhotoCachePayload(userId);
  if (!payload) return null;
  
  if (payload.kind === 'url') {
    return payload.url;
  }
  
  if (payload.kind === 'blob') {
    // V253: CRITICAL FIX - Check if object URL exists synchronously before returning
    // Never return a dead blob URL - if object URL is missing, return null immediately
    try {
      const { getProfileUrlForKey } = await import('./objectUrl');
      const key = profilePhotoUrlKey(userId);
      const existingUrl = getProfileUrlForKey(key);
      
      // If object URL exists, return it (blob is still valid)
      if (existingUrl) {
        return existingUrl;
      }
      
      // Object URL missing (blob was revoked or never created) - return null
      // This prevents ERR_FILE_NOT_FOUND errors from dead blob URLs
      if (import.meta.env.DEV) {
        console.warn('⚠️ V253: Blob cache entry exists but object URL is missing (revoked/dead) - returning null');
      }
      return null;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ V250: Failed to resolve blob from PhotoStore:', error);
      }
      return null;
    }
  }
  
  return null;
}

/**
 * V250: One-time migration helper.
 *
 * Moves legacy localStorage entry
 *   blissnet_cache_profile_photo_url_<userId>
 * to canonical key
 *   blissnet_cache_profile_photo:<userId>
 * if the canonical key is missing.
 *
 * Safe to call multiple times; it becomes a no-op after migration.
 */
export function migrateLegacyProfilePhotoCache(userId: string): void {
  if (typeof window === 'undefined' || !('localStorage' in window)) return;
  if (!userId) return;

  try {
    const legacyStorageKey = `${LEGACY_PROFILE_PHOTO_URL_PREFIX}${userId}`;
    const canonicalStorageKey = `${PERSISTENT_CACHE_PREFIX}${profilePhotoUrlKey(userId)}`;

    const legacyRaw = window.localStorage.getItem(legacyStorageKey);

    // If there's no legacy entry at all, just ensure it's removed and bail
    if (!legacyRaw) {
      if (window.localStorage.getItem(legacyStorageKey) !== null) {
        window.localStorage.removeItem(legacyStorageKey);
      }
      return;
    }

    const hasCanonical = window.localStorage.getItem(canonicalStorageKey) !== null;

    if (!hasCanonical) {
      window.localStorage.setItem(canonicalStorageKey, legacyRaw);
      if (import.meta.env.DEV) {
        console.info('✅ V250: Migrated legacy profile photo cache key → canonical', {
          userId,
          from: legacyStorageKey,
          to: canonicalStorageKey,
        });
      }
    }

    // Always remove legacy key once we've handled it
    window.localStorage.removeItem(legacyStorageKey);
  } catch (error) {
    // Migration failures are non-fatal; log in dev only
    if (import.meta.env.DEV) {
      console.warn('⚠️ V250: Profile photo cache migration failed (non-critical):', error);
    }
  }
}

